import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  Plus, X, FileText, Send, BarChart3, Target, Eye, Search as SearchIcon,
  ChevronRight, Check, Trash2, Copy, RefreshCw, Loader2, Mail, Calendar,
  Users, MapPin, DollarSign, Clock, AlertTriangle, ExternalLink,
  ChefHat, GripVertical, Sparkles, TrendingUp, Star, MessageSquare,
  ArrowRight, Building2, UtensilsCrossed
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUTS = ['brouillon', 'envoyee', 'relance', 'acceptee', 'refusee'];
const STATUT_COLORS = {
  brouillon: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200', dot: 'bg-gray-400' },
  envoyee: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
  relance: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
  acceptee: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  refusee: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', dot: 'bg-red-500' },
};
const STATUT_LABELS = { brouillon: 'Brouillon', envoyee: 'Envoyée', relance: 'Relance', acceptee: 'Acceptée', refusee: 'Refusée' };

const CARD_IMAGES = [
  'https://images.unsplash.com/photo-1769812344084-b45b638b1737?w=680&h=440&fit=crop&q=60',
  'https://images.unsplash.com/photo-1616668856493-9df876327739?w=680&h=440&fit=crop&q=60',
  'https://images.unsplash.com/photo-1693463399197-e6063452cf07?w=680&h=440&fit=crop&q=60',
];

const MAIL_TYPES = [
  { id: 'envoi', label: 'Envoi proposition', icon: Send, color: 'text-blue-600' },
  { id: 'relance_j5', label: 'Relance J+5', icon: Clock, color: 'text-amber-600' },
  { id: 'relance_j10', label: 'Relance J+10', icon: AlertTriangle, color: 'text-orange-600' },
  { id: 'confirmation', label: 'Confirmation', icon: Check, color: 'text-emerald-600' },
  { id: 'refus', label: 'Refus gracieux', icon: X, color: 'text-red-500' },
  { id: 'post_event', label: 'Post-événement', icon: Star, color: 'text-purple-600' },
];

const VEILLE_SECTIONS = [
  { id: 'concurrents', label: 'Concurrents locaux', icon: Building2, desc: 'Analyse des traiteurs du 66' },
  { id: 'tarifs', label: 'Tarifs marché', icon: DollarSign, desc: 'Positionnement prix' },
  { id: 'tendances', label: 'Tendances', icon: TrendingUp, desc: 'Tendances événementiel' },
  { id: 'avis', label: 'Avis Google', icon: Star, desc: 'E-réputation' },
];

// ═══════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════

export default function PropositionsPage() {
  const [tab, setTab] = useState('dashboard');
  const [propositions, setPropositions] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProp, setSelectedProp] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [typesEvenement, setTypesEvenement] = useState([]);

  const load = useCallback(async () => {
    try {
      const [pRes, cRes, sRes, tRes] = await Promise.all([
        fetch(`${API}/api/propositions`),
        fetch(`${API}/api/propositions/catalogue`),
        fetch(`${API}/api/propositions/stats/dashboard`),
        fetch(`${API}/api/propositions/types-evenement`),
      ]);
      setPropositions(await pRes.json());
      setCatalogue(await cRes.json());
      setStats(await sRes.json());
      setTypesEvenement(await tRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const TABS = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: BarChart3 },
    { id: 'restaurant', label: 'Restaurant', icon: UtensilsCrossed },
    { id: 'traiteur', label: 'Traiteur', icon: ChefHat },
    { id: 'emails', label: 'Emails', icon: Mail },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#009893]" size={32} /><span className="ml-3 text-sm text-gray-500">Chargement...</span></div>;

  return (
    <div className="space-y-5" data-testid="propositions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Georgia, serif' }}>Propositions</h1>
          <p className="text-xs text-gray-400">Le Mas — Restaurant & Traiteur</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#009893] hover:bg-[#007d79] text-white" data-testid="create-proposition-btn">
          <Plus size={16} className="mr-1" /> Nouvelle proposition
        </Button>
      </div>

      {/* Navigation tabs - bigger, clearer */}
      <div className="grid grid-cols-4 gap-2">
        {TABS.map(t => {
          const colors = {
            overview: '#1B2A52',
            restaurant: '#E07A5F',
            traiteur: '#7C3AED',
            emails: '#2563EB',
          };
          const isActive = tab === t.id;
          const color = colors[t.id] || '#009893';
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all border-2 ${isActive ? 'shadow-md text-white' : 'bg-white text-gray-500 hover:text-gray-700 border-gray-100'}`}
              style={isActive ? { background: color, borderColor: color } : {}}>
              <t.icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Overview - combined stats */}
      {tab === 'overview' && (
        <div className="space-y-4">
          <PropDashboard stats={stats} propositions={propositions} onSelect={setSelectedProp} />
        </div>
      )}

      {/* Restaurant - Menus Groupe + Pipeline */}
      {tab === 'restaurant' && <RestaurantSubTabs />}

      {/* Traiteur - Propositions + Pipeline + Catalogue */}
      {tab === 'traiteur' && (
        <div className="space-y-4">
          <TraiteurSubTabs propositions={propositions} catalogue={catalogue} typesEvenement={typesEvenement} stats={stats} onSelect={setSelectedProp} onRefresh={load} />
        </div>
      )}

      {/* Emails */}
      {tab === 'emails' && <SuiviEmailsView />}

      {showCreate && <CreateProposition catalogue={catalogue} typesEvenement={typesEvenement} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {selectedProp && <PropositionDetail prop={selectedProp} catalogue={catalogue} onClose={() => setSelectedProp(null)} onRefresh={() => { load(); setSelectedProp(null); }} />}
    </div>
  );
}


// ═══════════════════════════════════════════════
// DASHBOARD
// ═══════════════════════════════════════════════

function PropDashboard({ stats, propositions, onSelect }) {
  if (!stats) return null;
  const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

  return (
    <div className="space-y-4" data-testid="prop-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {STATUTS.map(s => {
          const c = STATUT_COLORS[s];
          return (
            <div key={s} className={`${c.bg} ${c.border} border rounded-xl p-3 text-center`} data-testid={`stat-${s}`}>
              <p className="text-2xl font-bold font-mono">{stats.compteurs[s] || 0}</p>
              <p className={`text-[10px] uppercase tracking-wider font-medium ${c.text}`}>{STATUT_LABELS[s]}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="bg-gradient-to-br from-[#009893]/10 to-[#009893]/5 border border-[#009893]/20 rounded-xl p-4">
          <p className="text-[10px] uppercase tracking-wider text-[#009893] font-medium mb-1">CA Potentiel</p>
          <p className="text-2xl font-bold text-[#009893] font-mono" data-testid="ca-potentiel">{fmt(stats.ca_potentiel)}</p>
          <p className="text-[10px] text-gray-400">Envoyées + Acceptées</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-25 border border-emerald-200 rounded-xl p-4">
          <p className="text-[10px] uppercase tracking-wider text-emerald-600 font-medium mb-1">CA Confirmé</p>
          <p className="text-2xl font-bold text-emerald-700 font-mono" data-testid="ca-confirme">{fmt(stats.ca_confirme)}</p>
          <p className="text-[10px] text-gray-400">Acceptées uniquement</p>
        </div>
      </div>

      {stats.alertes_relance?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2" data-testid="alertes-relance">
          <h3 className="text-xs font-bold text-amber-700 flex items-center gap-1.5"><AlertTriangle size={14} /> Alertes relance</h3>
          {stats.alertes_relance.map((a, i) => (
            <div key={i} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-amber-100">
              <div>
                <p className="text-xs font-medium text-gray-900">{a.client}</p>
                <p className="text-[10px] text-amber-600">Envoyée depuis {a.jours} jours — {fmt(a.montant)}</p>
              </div>
              <button onClick={() => onSelect(propositions.find(p => p.id === a.id))} className="text-[10px] text-[#009893] hover:underline">Voir</button>
            </div>
          ))}
        </div>
      )}

      {stats.prochains_7j?.length > 0 && (
        <div className="bg-white border rounded-xl p-4 space-y-2" data-testid="prochains-events">
          <h3 className="text-xs font-bold text-gray-700 flex items-center gap-1.5"><Calendar size={14} /> Prochains événements (7j)</h3>
          {stats.prochains_7j.map((e, i) => (
            <div key={i} className="flex items-center justify-between bg-emerald-50 rounded-lg px-3 py-2 border border-emerald-100">
              <div>
                <p className="text-xs font-medium text-gray-900">{e.client} — {e.type}</p>
                <p className="text-[10px] text-emerald-600">{new Date(e.date).toLocaleDateString('fr-FR')} — {fmt(e.montant)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {propositions.length > 0 && (
        <div>
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Dernières propositions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {propositions.slice(0, 6).map((p, i) => (
              <PropCard key={p.id} prop={p} imageIndex={i} onClick={() => onSelect(p)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════
// PREMIUM CARD
// ═══════════════════════════════════════════════

function PropCard({ prop, imageIndex = 0, onClick }) {
  const c = STATUT_COLORS[prop.statut] || STATUT_COLORS.brouillon;
  const img = CARD_IMAGES[imageIndex % CARD_IMAGES.length];
  const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);
  const dateStr = prop.date_evenement ? new Date(prop.date_evenement).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  return (
    <button onClick={onClick} data-testid={`prop-card-${prop.id}`}
      className="group relative w-full rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-left"
      style={{ minHeight: '220px' }}>
      <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#009893] transition-opacity duration-300" style={{ opacity: 0.55 }} />
      <div className="absolute inset-0 bg-[#009893] opacity-0 group-hover:opacity-10 transition-opacity duration-300" />
      <div className="relative z-10 p-5 h-full flex flex-col justify-between" style={{ minHeight: '220px' }}>
        <div className="flex justify-between items-start">
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-bold text-white truncate" style={{ fontFamily: 'Georgia, serif' }}>{prop.client_nom || 'Client'}</h3>
            <p className="text-white/80 text-xs mt-0.5">{prop.type_evenement}</p>
          </div>
          <span className={`${c.bg} ${c.text} text-[10px] font-bold uppercase px-2.5 py-1 rounded-full tracking-wider shrink-0 ml-2`}>
            {STATUT_LABELS[prop.statut]}
          </span>
        </div>
        <div className="flex items-end justify-between mt-auto">
          <div className="space-y-0.5">
            {dateStr && <p className="text-white/90 text-xs flex items-center gap-1"><Calendar size={11} /> {dateStr}</p>}
            {prop.nb_personnes > 0 && <p className="text-white/70 text-[10px] flex items-center gap-1"><Users size={10} /> {prop.nb_personnes} personnes</p>}
          </div>
          <p className="text-white font-bold text-lg font-mono">{fmt(prop.total_ttc)}</p>
        </div>
      </div>
    </button>
  );
}


// ═══════════════════════════════════════════════
// PIPELINE KANBAN
// ═══════════════════════════════════════════════

function PropPipeline({ propositions, onSelect, onRefresh }) {
  const changeStatut = async (propId, newStatut) => {
    try {
      await fetch(`${API}/api/propositions/${propId}/statut`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ statut: newStatut }),
      });
      toast.success(`Statut → ${STATUT_LABELS[newStatut]}`);
      onRefresh();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-4" data-testid="prop-pipeline" style={{ minHeight: '60vh' }}>
      {STATUTS.map(statut => {
        const items = propositions.filter(p => p.statut === statut);
        const c = STATUT_COLORS[statut];
        const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);
        const total = items.reduce((s, p) => s + (p.total_ttc || 0), 0);

        return (
          <div key={statut} className="flex-shrink-0 w-64 bg-gray-50 rounded-xl p-3 space-y-2" data-testid={`pipeline-col-${statut}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1.5">
                <div className={`w-2 h-2 rounded-full ${c.dot}`} />
                <h3 className={`text-xs font-bold uppercase tracking-wider ${c.text}`}>{STATUT_LABELS[statut]}</h3>
              </div>
              <span className="text-[10px] font-mono text-gray-400">{items.length}</span>
            </div>
            {total > 0 && <p className="text-[10px] font-mono text-gray-400 -mt-1">{fmt(total)}</p>}

            <div className="space-y-2">
              {items.map(p => {
                const dateStr = p.date_evenement ? new Date(p.date_evenement).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '';
                const nextStatuts = STATUTS.filter(s => s !== statut);

                return (
                  <div key={p.id} className="bg-white rounded-lg border p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer group" data-testid={`pipeline-card-${p.id}`}>
                    <div onClick={() => onSelect(p)} className="mb-2">
                      <p className="text-xs font-bold text-gray-900 truncate">{p.client_nom}</p>
                      <p className="text-[10px] text-gray-500">{p.type_evenement}</p>
                      <div className="flex items-center justify-between mt-1.5">
                        {dateStr && <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><Calendar size={9} /> {dateStr}</span>}
                        <span className="text-xs font-mono font-bold text-[#009893]">{fmt(p.total_ttc)}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 opacity-0 group-hover:opacity-100 transition-opacity border-t pt-2 mt-1">
                      {nextStatuts.map(ns => (
                        <button key={ns} onClick={() => changeStatut(p.id, ns)}
                          className={`text-[9px] px-1.5 py-0.5 rounded ${STATUT_COLORS[ns].bg} ${STATUT_COLORS[ns].text} hover:opacity-80`}>
                          {STATUT_LABELS[ns]}
                        </button>
                      ))}
                      {statut === 'acceptee' && (
                        <a href="https://www.axonaut.com" target="_blank" rel="noopener noreferrer"
                          className="text-[9px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 flex items-center gap-0.5">
                          Axonaut <ExternalLink size={8} />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ═══════════════════════════════════════════════
// CREATE PROPOSITION MODAL
// ═══════════════════════════════════════════════

function CreateProposition({ catalogue, typesEvenement, onClose, onCreated }) {
  const [form, setForm] = useState({ client_nom: '', client_email: '', client_tel: '', type_evenement: '', date_evenement: '', nb_personnes: '', lieu: '', notes: '' });
  const [prestations, setPrestations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [catFilter, setCatFilter] = useState('');
  const [showCat, setShowCat] = useState(true);
  const [searchCat, setSearchCat] = useState('');
  const [suggestedCats, setSuggestedCats] = useState([]);
  const [loadingIA, setLoadingIA] = useState(false);
  const [iaConseil, setIaConseil] = useState('');

  const categories = [...new Set(catalogue.map(c => c.categorie))];

  // Auto-filter by event type
  useEffect(() => {
    if (form.type_evenement) {
      fetch(`${API}/api/propositions/catalogue/by-event/${encodeURIComponent(form.type_evenement)}`)
        .then(r => r.json())
        .then(data => {
          setSuggestedCats(data.suggested_categories || []);
          if (data.suggested_categories?.length > 0 && !catFilter) {
            setCatFilter(data.suggested_categories[0]);
          }
        })
        .catch(() => {});
    } else {
      setSuggestedCats([]);
    }
  }, [form.type_evenement]);

  const filteredCat = catalogue.filter(c => {
    const matchCat = !catFilter || c.categorie === catFilter;
    const matchSearch = !searchCat || c.nom.toLowerCase().includes(searchCat.toLowerCase()) || c.desc.toLowerCase().includes(searchCat.toLowerCase());
    return matchCat && matchSearch;
  });

  const addPrestation = (item) => {
    const existing = prestations.find(p => p.ref === item.ref);
    if (existing) {
      setPrestations(prestations.map(p => p.ref === item.ref ? { ...p, quantite: p.quantite + 1 } : p));
    } else {
      setPrestations([...prestations, { ...item, quantite: 1, sous_total: item.prix_ttc }]);
    }
  };

  const updateQty = (ref, qty) => {
    if (qty <= 0) {
      setPrestations(prestations.filter(p => p.ref !== ref));
    } else {
      setPrestations(prestations.map(p => p.ref === ref ? { ...p, quantite: qty, sous_total: qty * p.prix_ttc } : p));
    }
  };

  const removePrestation = (ref) => {
    setPrestations(prestations.filter(p => p.ref !== ref));
  };

  // IA Suggestion
  const askIA = async () => {
    if (!form.type_evenement) { toast.error('Choisissez d\'abord le type d\'événement'); return; }
    setLoadingIA(true);
    setIaConseil('');
    try {
      const res = await fetch(`${API}/api/propositions/ia-suggestion`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type_evenement: form.type_evenement, nb_personnes: parseInt(form.nb_personnes) || 50, budget: '', notes: form.notes }),
      });
      const data = await res.json();
      if (data.suggestions && !data.raw) {
        const newPrests = [];
        for (const s of data.suggestions) {
          const catItem = catalogue.find(c => c.ref === s.ref);
          if (catItem) {
            newPrests.push({ ...catItem, quantite: s.quantite || 1, sous_total: catItem.prix_ttc * (s.quantite || 1), raison: s.raison });
          }
        }
        setPrestations(newPrests);
        if (data.conseil) setIaConseil(data.conseil);
        toast.success(`${newPrests.length} prestations suggérées par l'IA`);
      } else {
        toast.info('L\'IA a donné un conseil général');
        if (data.conseil) setIaConseil(data.conseil);
      }
    } catch { toast.error('Erreur IA'); }
    setLoadingIA(false);
  };

  const nbPers = parseInt(form.nb_personnes) || 1;
  const totalTTC = prestations.reduce((sum, p) => {
    if (p.unite === '€/pers') return sum + p.prix_ttc * p.quantite * nbPers;
    return sum + p.prix_ttc * p.quantite;
  }, 0);

  const handleSubmit = async () => {
    if (!form.client_nom || !form.type_evenement) { toast.error('Nom client et type requis'); return; }
    setSaving(true);
    try {
      const prestsWithTotals = prestations.map(p => ({
        ref: p.ref, nom: p.nom, categorie: p.categorie, prix_ttc: p.prix_ttc, unite: p.unite, quantite: p.quantite,
        sous_total: p.unite === '€/pers' ? p.prix_ttc * p.quantite * nbPers : p.prix_ttc * p.quantite,
      }));
      await fetch(`${API}/api/propositions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, nb_personnes: nbPers, prestations: prestsWithTotals, total_ttc: totalTTC }),
      });
      toast.success('Proposition créée');
      onCreated();
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto py-8" data-testid="create-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl mx-4">
        <div className="flex items-center justify-between p-5 border-b bg-gradient-to-r from-[#009893]/5 to-transparent rounded-t-2xl">
          <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>Nouvelle proposition</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* Client info */}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[10px] text-gray-400 block mb-0.5">Nom client *</label>
              <Input value={form.client_nom} onChange={e => setForm(f => ({ ...f, client_nom: e.target.value }))} className="text-sm h-9" data-testid="input-client-nom" /></div>
            <div><label className="text-[10px] text-gray-400 block mb-0.5">Email</label>
              <Input type="email" value={form.client_email} onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))} className="text-sm h-9" /></div>
            <div><label className="text-[10px] text-gray-400 block mb-0.5">Téléphone</label>
              <Input value={form.client_tel} onChange={e => setForm(f => ({ ...f, client_tel: e.target.value }))} className="text-sm h-9" /></div>
            <div><label className="text-[10px] text-gray-400 block mb-0.5">Type d'événement *</label>
              <select value={form.type_evenement} onChange={e => setForm(f => ({ ...f, type_evenement: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm h-9 bg-white" data-testid="input-type-event">
                <option value="">Sélectionner...</option>
                {typesEvenement.map(t => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><label className="text-[10px] text-gray-400 block mb-0.5">Date événement</label>
              <Input type="date" value={form.date_evenement} onChange={e => setForm(f => ({ ...f, date_evenement: e.target.value }))} className="text-sm h-9" data-testid="input-date" /></div>
            <div><label className="text-[10px] text-gray-400 block mb-0.5">Nombre de personnes</label>
              <Input type="number" value={form.nb_personnes} onChange={e => setForm(f => ({ ...f, nb_personnes: e.target.value }))} className="text-sm h-9" data-testid="input-nb-pers" /></div>
            <div className="col-span-2"><label className="text-[10px] text-gray-400 block mb-0.5">Lieu</label>
              <Input value={form.lieu} onChange={e => setForm(f => ({ ...f, lieu: e.target.value }))} placeholder="Sur place / adresse..." className="text-sm h-9" /></div>
          </div>

          {/* IA Suggestion button */}
          <div className="flex items-center gap-3">
            <Button onClick={askIA} disabled={loadingIA || !form.type_evenement} variant="outline" className="border-[#009893] text-[#009893] hover:bg-[#009893]/5" data-testid="ia-suggest-btn">
              {loadingIA ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />}
              {loadingIA ? 'L\'IA réfléchit...' : 'Suggestion IA'}
            </Button>
            {form.type_evenement && <span className="text-[10px] text-gray-400">L'IA propose une sélection adaptée à un {form.type_evenement.toLowerCase()}</span>}
          </div>

          {iaConseil && (
            <div className="bg-[#009893]/5 border border-[#009893]/20 rounded-xl p-3 text-xs text-gray-700 italic" data-testid="ia-conseil">
              <Sparkles size={12} className="inline mr-1 text-[#009893]" /> {iaConseil}
            </div>
          )}

          {/* Catalogue browser */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                <ChefHat size={14} className="text-[#009893]" /> Catalogue — sélectionnez les prestations
              </h3>
              <button onClick={() => setShowCat(!showCat)} className="text-[10px] text-[#009893] hover:underline">{showCat ? 'Masquer' : 'Afficher'}</button>
            </div>

            {showCat && (
              <div className="bg-gray-50 rounded-xl p-3 mb-3 space-y-2 border">
                {/* Search */}
                <div className="relative">
                  <SearchIcon size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input value={searchCat} onChange={e => setSearchCat(e.target.value)} placeholder="Rechercher..." className="pl-8 text-xs h-8" />
                </div>

                {/* Category filters — suggested first */}
                <div className="flex gap-1 flex-wrap">
                  <button onClick={() => setCatFilter('')} className={`text-[9px] px-2 py-0.5 rounded-full font-medium ${!catFilter ? 'bg-[#009893] text-white' : 'bg-gray-200 text-gray-600'}`}>Tout</button>
                  {suggestedCats.length > 0 && suggestedCats.map(cat => (
                    <button key={cat} onClick={() => setCatFilter(f => f === cat ? '' : cat)}
                      className={`text-[9px] px-2 py-0.5 rounded-full font-medium ring-1 ring-[#009893]/30 ${catFilter === cat ? 'bg-[#009893] text-white' : 'bg-[#009893]/10 text-[#009893]'}`}>
                      {cat}
                    </button>
                  ))}
                  {categories.filter(c => !suggestedCats.includes(c)).map(cat => (
                    <button key={cat} onClick={() => setCatFilter(f => f === cat ? '' : cat)}
                      className={`text-[9px] px-2 py-0.5 rounded-full font-medium ${catFilter === cat ? 'bg-[#009893] text-white' : 'bg-gray-200 text-gray-600'}`}>
                      {cat}
                    </button>
                  ))}
                </div>

                {suggestedCats.length > 0 && !catFilter && (
                  <p className="text-[9px] text-[#009893] italic">Les catégories recommandées pour un {form.type_evenement} sont mises en avant</p>
                )}

                {/* Items list */}
                <div className="max-h-52 overflow-y-auto space-y-1">
                  {filteredCat.map(item => {
                    const isSelected = prestations.some(p => p.ref === item.ref);
                    return (
                      <div key={item.ref}
                        className={`flex items-center justify-between rounded-lg px-3 py-1.5 cursor-pointer border transition-all ${isSelected ? 'bg-[#009893]/10 border-[#009893]/30' : 'bg-white hover:bg-[#009893]/5 border-gray-100'}`}
                        onClick={() => addPrestation(item)} data-testid={`cat-add-${item.ref}`}>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {isSelected && <Check size={12} className="text-[#009893] shrink-0" />}
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-gray-900 truncate">{item.nom}</p>
                            <p className="text-[10px] text-gray-400 truncate">{item.desc?.slice(0, 70)}</p>
                          </div>
                        </div>
                        <div className="text-right shrink-0 ml-2">
                          <p className="text-xs font-mono font-bold text-[#009893]">{item.prix_ttc}€</p>
                          <p className="text-[9px] text-gray-400">{item.unite}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Selected prestations */}
            {prestations.length > 0 && (
              <div className="space-y-1.5">
                <h4 className="text-[10px] text-gray-400 uppercase tracking-wider">Prestations sélectionnées ({prestations.length})</h4>
                {prestations.map(p => (
                  <div key={p.ref} className="flex items-center justify-between bg-[#009893]/5 rounded-lg px-3 py-2 border border-[#009893]/10">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-900">{p.nom}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-[10px] text-gray-400">{p.prix_ttc}€ {p.unite}{p.unite === '€/pers' ? ` x ${nbPers} pers` : ''}</p>
                        {p.raison && <p className="text-[9px] text-[#009893] italic">IA: {p.raison}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <button onClick={() => updateQty(p.ref, p.quantite - 1)} className="w-6 h-6 rounded bg-gray-200 text-xs font-bold hover:bg-gray-300 transition-colors">-</button>
                      <span className="text-xs font-mono w-6 text-center">{p.quantite}</span>
                      <button onClick={() => updateQty(p.ref, p.quantite + 1)} className="w-6 h-6 rounded bg-gray-200 text-xs font-bold hover:bg-gray-300 transition-colors">+</button>
                      <span className="text-xs font-mono font-bold text-[#009893] w-16 text-right">
                        {fmt(p.unite === '€/pers' ? p.prix_ttc * p.quantite * nbPers : p.prix_ttc * p.quantite)}
                      </span>
                      <button onClick={() => removePrestation(p.ref)} className="text-red-300 hover:text-red-500 ml-1"><X size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div><label className="text-[10px] text-gray-400 block mb-0.5">Notes / Remarques</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm resize-none" placeholder="Allergies, demandes spéciales..." /></div>
        </div>

        <div className="flex items-center justify-between p-5 border-t bg-gray-50 rounded-b-2xl">
          <div>
            <p className="text-xs text-gray-400">Total TTC</p>
            <p className="text-xl font-bold text-[#009893] font-mono" data-testid="total-ttc">{fmt(totalTTC)}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-[#009893] hover:bg-[#007d79] text-white" data-testid="save-proposition-btn">
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />} Créer la proposition
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════
// PROPOSITION DETAIL MODAL
// ═══════════════════════════════════════════════

function PropositionDetail({ prop, catalogue, onClose, onRefresh }) {
  const [activeTab, setActiveTab] = useState('infos');
  const [mailType, setMailType] = useState('envoi');
  const [mailContent, setMailContent] = useState(null);
  const [mailSubject, setMailSubject] = useState('');
  const [mailBody, setMailBody] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

  const generateMail = async (type) => {
    setGenerating(true);
    setMailType(type);
    try {
      const res = await fetch(`${API}/api/propositions/${prop.id}/generate-mail`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type }),
      });
      const data = await res.json();
      if (res.ok) {
        setMailContent(data);
        setMailSubject(data.subject);
        setMailBody(data.body);
        toast.success('Email généré par l\'IA');
      } else {
        toast.error(data.detail || 'Erreur IA');
      }
    } catch { toast.error('Erreur réseau'); }
    setGenerating(false);
  };

  const sendMail = async () => {
    if (!prop.client_email) { toast.error('Email client requis'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/propositions/${prop.id}/send-mail`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: prop.client_email, subject: mailSubject, body: mailBody, type: mailType }),
      });
      const data = await res.json();
      if (res.ok) { toast.success(data.message); onRefresh(); }
      else { toast.error(data.detail || 'Erreur envoi'); }
    } catch { toast.error('Erreur réseau'); }
    setSending(false);
  };

  const copyMail = () => {
    navigator.clipboard.writeText(mailBody);
    toast.success('Copié dans le presse-papier');
  };

  const handleDelete = async () => {
    if (!confirm('Supprimer cette proposition ?')) return;
    setDeleting(true);
    try {
      await fetch(`${API}/api/propositions/${prop.id}`, { method: 'DELETE' });
      toast.success('Proposition supprimée');
      onRefresh();
    } catch { toast.error('Erreur'); }
    setDeleting(false);
  };

  const changeStatut = async (newStatut) => {
    try {
      await fetch(`${API}/api/propositions/${prop.id}/statut`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ statut: newStatut }),
      });
      toast.success(`Statut → ${STATUT_LABELS[newStatut]}`);
      onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const c = STATUT_COLORS[prop.statut] || STATUT_COLORS.brouillon;
  const DETAIL_TABS = [
    { id: 'infos', label: 'Infos', icon: FileText },
    { id: 'mails', label: 'Emails IA', icon: Mail },
    { id: 'historique', label: 'Historique', icon: Clock },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto py-8" data-testid="prop-detail-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4">
        <div className="p-5 border-b bg-gradient-to-r from-[#009893]/5 to-transparent rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>{prop.client_nom}</h2>
              <p className="text-xs text-gray-500">{prop.type_evenement} — {prop.date_evenement ? new Date(prop.date_evenement).toLocaleDateString('fr-FR') : 'Date à définir'}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`${c.bg} ${c.text} text-[10px] font-bold uppercase px-2.5 py-1 rounded-full`}>{STATUT_LABELS[prop.statut]}</span>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
          </div>
          <div className="flex gap-1 mt-3">
            {STATUTS.filter(s => s !== prop.statut).map(s => (
              <button key={s} onClick={() => changeStatut(s)}
                className={`text-[9px] px-2 py-0.5 rounded-full ${STATUT_COLORS[s].bg} ${STATUT_COLORS[s].text}`}>
                → {STATUT_LABELS[s]}
              </button>
            ))}
            {prop.statut === 'acceptee' && (
              <a href="https://www.axonaut.com" target="_blank" rel="noopener noreferrer"
                className="text-[9px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 flex items-center gap-0.5">
                Axonaut <ExternalLink size={8} />
              </a>
            )}
          </div>
        </div>

        <div className="flex border-b">
          {DETAIL_TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${activeTab === t.id ? 'text-[#009893] border-[#009893]' : 'text-gray-400 border-transparent hover:text-gray-600'}`}>
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </div>

        <div className="p-5 max-h-[55vh] overflow-y-auto">
          {activeTab === 'infos' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-gray-400 block text-[10px]">Client</span><span className="font-medium">{prop.client_nom}</span></div>
                <div><span className="text-gray-400 block text-[10px]">Email</span><span className="font-medium">{prop.client_email || '—'}</span></div>
                <div><span className="text-gray-400 block text-[10px]">Téléphone</span><span className="font-medium">{prop.client_tel || '—'}</span></div>
                <div><span className="text-gray-400 block text-[10px]">Personnes</span><span className="font-medium">{prop.nb_personnes || '—'}</span></div>
                <div><span className="text-gray-400 block text-[10px]">Lieu</span><span className="font-medium">{prop.lieu || '—'}</span></div>
                <div><span className="text-gray-400 block text-[10px]">Créée le</span><span className="font-medium">{prop.created_at ? new Date(prop.created_at).toLocaleDateString('fr-FR') : '—'}</span></div>
              </div>
              {prop.prestations?.length > 0 && (
                <div>
                  <h4 className="text-[10px] text-gray-400 uppercase tracking-wider mb-2">Prestations</h4>
                  <div className="space-y-1">
                    {prop.prestations.map((p, i) => (
                      <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-1.5 text-xs">
                        <span className="text-gray-700">{p.nom} x{p.quantite}</span>
                        <span className="font-mono font-bold text-[#009893]">{fmt(p.sous_total)}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between bg-[#009893]/10 rounded-lg px-3 py-2 text-sm font-bold mt-1">
                      <span className="text-gray-700">Total TTC</span>
                      <span className="font-mono text-[#009893]">{fmt(prop.total_ttc)}</span>
                    </div>
                  </div>
                </div>
              )}
              {prop.notes && <div><h4 className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Notes</h4><p className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3">{prop.notes}</p></div>}
              <button onClick={handleDelete} disabled={deleting} className="text-[10px] text-red-400 hover:text-red-600 flex items-center gap-1 mt-2">
                <Trash2 size={11} /> Supprimer cette proposition
              </button>
            </div>
          )}

          {activeTab === 'mails' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                {MAIL_TYPES.map(mt => (
                  <button key={mt.id} onClick={() => generateMail(mt.id)} disabled={generating}
                    className={`flex items-center gap-1.5 p-2.5 rounded-lg border text-left transition-all hover:shadow-sm ${mailType === mt.id && mailContent ? 'border-[#009893] bg-[#009893]/5' : 'border-gray-200 hover:border-gray-300'}`}
                    data-testid={`mail-btn-${mt.id}`}>
                    <mt.icon size={14} className={mt.color} />
                    <span className="text-[10px] font-medium text-gray-700">{mt.label}</span>
                  </button>
                ))}
              </div>

              {generating && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="animate-spin text-[#009893] mr-2" size={20} />
                  <span className="text-sm text-gray-500">L'IA rédige votre email...</span>
                </div>
              )}

              {mailContent && !generating && (
                <div className="space-y-3">
                  <div><label className="text-[10px] text-gray-400 block mb-0.5">Objet</label>
                    <Input value={mailSubject} onChange={e => setMailSubject(e.target.value)} className="text-sm" /></div>
                  <div><label className="text-[10px] text-gray-400 block mb-0.5">Corps de l'email</label>
                    <textarea value={mailBody} onChange={e => setMailBody(e.target.value)} rows={10}
                      className="w-full border rounded-lg px-3 py-2 text-sm resize-none" style={{ fontFamily: 'Georgia, serif', lineHeight: '1.7' }} /></div>
                  <div className="flex gap-2 flex-wrap">
                    <Button onClick={() => generateMail(mailType)} variant="outline" size="sm" className="text-xs" data-testid="regenerate-mail-btn">
                      <RefreshCw size={12} className="mr-1" /> Regénérer
                    </Button>
                    <Button onClick={copyMail} variant="outline" size="sm" className="text-xs">
                      <Copy size={12} className="mr-1" /> Copier
                    </Button>
                    {prop.client_email && (
                      <Button onClick={sendMail} disabled={sending} size="sm" className="bg-[#009893] hover:bg-[#007d79] text-white text-xs" data-testid="send-mail-btn">
                        {sending ? <Loader2 size={12} className="animate-spin mr-1" /> : <Send size={12} className="mr-1" />} Envoyer à {prop.client_email}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {prop.emails_envoyes?.length > 0 && (
                <div>
                  <h4 className="text-[10px] text-gray-400 uppercase tracking-wider mb-2 mt-4">Emails envoyés</h4>
                  <div className="space-y-1">
                    {prop.emails_envoyes.map((e, i) => (
                      <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-1.5 text-[10px]">
                        <span className="text-gray-600">{e.type} → {e.to}</span>
                        <span className="text-gray-400">{new Date(e.date).toLocaleDateString('fr-FR')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'historique' && (
            <div className="space-y-1.5">
              {(prop.historique || []).slice().reverse().map((h, i) => (
                <div key={i} className="flex items-start gap-3 bg-gray-50 rounded-lg px-3 py-2">
                  <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${STATUT_COLORS[h.statut]?.dot || 'bg-gray-400'}`} />
                  <div>
                    <p className="text-xs text-gray-700">{h.action}</p>
                    <p className="text-[10px] text-gray-400">{new Date(h.date).toLocaleString('fr-FR')}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════
// CATALOGUE VIEW
// ═══════════════════════════════════════════════

function CatalogueView({ catalogue, onRefresh }) {
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [newItem, setNewItem] = useState({ nom: '', desc: '', prix_ttc: '', unite: '€/pers', categorie: '', ref: '' });
  const [saving, setSaving] = useState(false);
  const [pdfMode, setPdfMode] = useState(false);
  const [selectedRefs, setSelectedRefs] = useState(new Set());
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const categories = [...new Set(catalogue.map(c => c.categorie))];
  const filtered = catalogue.filter(item => {
    const matchCat = !filter || item.categorie === filter;
    const matchSearch = !search || item.nom.toLowerCase().includes(search.toLowerCase()) || item.desc.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const grouped = categories.reduce((acc, cat) => {
    const items = filtered.filter(i => i.categorie === cat);
    if (items.length > 0) acc[cat] = items;
    return acc;
  }, {});

  const catColors = {
    "Vins d'honneur": 'from-amber-50 to-amber-25 border-amber-200',
    "Formules mariage": 'from-pink-50 to-pink-25 border-pink-200',
    "Buffets froid": 'from-sky-50 to-sky-25 border-sky-200',
    "Plats cuisinés": 'from-orange-50 to-orange-25 border-orange-200',
    "Brunch": 'from-yellow-50 to-yellow-25 border-yellow-200',
    "Ateliers": 'from-emerald-50 to-emerald-25 border-emerald-200',
    "Boissons": 'from-purple-50 to-purple-25 border-purple-200',
    "Plateaux": 'from-rose-50 to-rose-25 border-rose-200',
    "Pains": 'from-amber-50 to-amber-25 border-amber-200',
    "Desserts": 'from-fuchsia-50 to-fuchsia-25 border-fuchsia-200',
    "Personnel": 'from-slate-50 to-slate-25 border-slate-200',
    "Privatisation": 'from-indigo-50 to-indigo-25 border-indigo-200',
    "Livraison": 'from-teal-50 to-teal-25 border-teal-200',
  };

  // PDF mode handlers
  const toggleRef = (ref) => {
    setSelectedRefs(prev => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });
  };

  const selectAll = () => setSelectedRefs(new Set(catalogue.map(c => c.ref)));
  const selectNone = () => setSelectedRefs(new Set());
  const selectCategory = (cat) => {
    const refs = catalogue.filter(c => c.categorie === cat).map(c => c.ref);
    setSelectedRefs(prev => {
      const next = new Set(prev);
      const allSelected = refs.every(r => next.has(r));
      refs.forEach(r => allSelected ? next.delete(r) : next.add(r));
      return next;
    });
  };

  const generatePdf = async () => {
    setGeneratingPdf(true);
    try {
      const body = selectedRefs.size > 0 && selectedRefs.size < catalogue.length
        ? { selected_refs: [...selectedRefs] }
        : {};
      const res = await fetch(`${API}/api/catalogue-traiteur-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        toast.success('PDF généré');
      } else {
        toast.error('Erreur génération PDF');
      }
    } catch { toast.error('Erreur'); }
    setGeneratingPdf(false);
  };

  const enterPdfMode = () => {
    setPdfMode(true);
    setSelectedRefs(new Set(catalogue.map(c => c.ref)));
  };

  const handleAdd = async () => {
    if (!newItem.nom || !newItem.prix_ttc) { toast.error('Nom et prix requis'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/propositions/catalogue`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newItem, prix_ttc: parseFloat(newItem.prix_ttc) }),
      });
      if (res.ok) { toast.success('Article ajouté'); setShowAdd(false); setNewItem({ nom: '', desc: '', prix_ttc: '', unite: '€/pers', categorie: '', ref: '' }); onRefresh(); }
      else { const d = await res.json(); toast.error(d.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const handleEdit = async () => {
    if (!editItem.nom || !editItem.prix_ttc) { toast.error('Nom et prix requis'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/propositions/catalogue/${editItem.ref}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nom: editItem.nom, desc: editItem.desc, prix_ttc: parseFloat(editItem.prix_ttc), unite: editItem.unite, categorie: editItem.categorie }),
      });
      if (res.ok) { toast.success('Article modifié'); setEditItem(null); onRefresh(); }
      else { toast.error('Erreur modification'); }
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const handleDelete = async (ref) => {
    if (!confirm('Supprimer cet article du catalogue ?')) return;
    try {
      const res = await fetch(`${API}/api/propositions/catalogue/${ref}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Article supprimé'); onRefresh(); }
      else { toast.error('Erreur suppression'); }
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="space-y-4" data-testid="catalogue-view">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher une prestation..." className="pl-9 text-sm h-9" data-testid="catalogue-search" />
        </div>
        <p className="text-xs text-gray-400 shrink-0">{filtered.length} / {catalogue.length}</p>
        {!pdfMode ? (
          <>
            <Button onClick={enterPdfMode} variant="outline" size="sm" className="text-xs border-[#009893] text-[#009893]" data-testid="pdf-mode-btn">
              <FileText size={13} className="mr-1" /> Générer PDF
            </Button>
            <Button onClick={() => setShowAdd(true)} size="sm" className="bg-[#009893] hover:bg-[#007d79] text-white text-xs" data-testid="add-catalogue-btn">
              <Plus size={13} className="mr-1" /> Ajouter
            </Button>
          </>
        ) : (
          <>
            <Button onClick={generatePdf} disabled={generatingPdf || selectedRefs.size === 0} size="sm" className="bg-[#009893] hover:bg-[#007d79] text-white text-xs" data-testid="generate-pdf-btn">
              {generatingPdf ? <Loader2 size={13} className="animate-spin mr-1" /> : <FileText size={13} className="mr-1" />}
              PDF ({selectedRefs.size} articles)
            </Button>
            <Button onClick={() => setPdfMode(false)} variant="outline" size="sm" className="text-xs">
              <X size={13} className="mr-1" /> Annuler
            </Button>
          </>
        )}
      </div>

      {pdfMode && (
        <div className="flex items-center gap-2 bg-[#009893]/5 border border-[#009893]/20 rounded-lg px-3 py-2" data-testid="pdf-selection-bar">
          <span className="text-xs text-[#009893] font-medium">Sélectionnez les articles à inclure dans le PDF :</span>
          <button onClick={selectAll} className="text-[10px] text-[#009893] hover:underline font-medium">Tout</button>
          <span className="text-gray-300">|</span>
          <button onClick={selectNone} className="text-[10px] text-red-400 hover:underline">Aucun</button>
          <span className="text-gray-300">|</span>
          {categories.map(cat => (
            <button key={cat} onClick={() => selectCategory(cat)}
              className={`text-[9px] px-1.5 py-0.5 rounded ${catalogue.filter(c => c.categorie === cat).every(c => selectedRefs.has(c.ref)) ? 'bg-[#009893] text-white' : 'bg-gray-200 text-gray-600'}`}>
              {cat}
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-1.5 flex-wrap">
        <button onClick={() => setFilter('')} className={`text-[10px] px-3 py-1 rounded-full font-medium transition-all ${!filter ? 'bg-[#009893] text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          Tout ({catalogue.length})
        </button>
        {categories.map(cat => {
          const count = catalogue.filter(i => i.categorie === cat).length;
          return (
            <button key={cat} onClick={() => setFilter(f => f === cat ? '' : cat)}
              className={`text-[10px] px-3 py-1 rounded-full font-medium transition-all ${filter === cat ? 'bg-[#009893] text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {cat} ({count})
            </button>
          );
        })}
      </div>

      {/* Add / Edit form */}
      {(showAdd || editItem) && (
        <div className="bg-white border-2 border-[#009893] rounded-xl p-4 space-y-3" data-testid="catalogue-form">
          <h4 className="text-xs font-bold text-gray-700">{editItem ? 'Modifier l\'article' : 'Nouvel article'}</h4>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[10px] text-gray-400 block mb-0.5">Nom *</label>
              <Input value={editItem ? editItem.nom : newItem.nom} onChange={e => editItem ? setEditItem({ ...editItem, nom: e.target.value }) : setNewItem({ ...newItem, nom: e.target.value })} className="text-sm h-8" /></div>
            <div><label className="text-[10px] text-gray-400 block mb-0.5">Catégorie</label>
              <select value={editItem ? editItem.categorie : newItem.categorie} onChange={e => editItem ? setEditItem({ ...editItem, categorie: e.target.value }) : setNewItem({ ...newItem, categorie: e.target.value })} className="w-full border rounded-lg px-2 py-1.5 text-sm h-8 bg-white">
                <option value="">Sélectionner...</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
                <option value="Autre">Autre</option>
              </select></div>
            <div><label className="text-[10px] text-gray-400 block mb-0.5">Prix TTC *</label>
              <Input type="number" step="0.01" value={editItem ? editItem.prix_ttc : newItem.prix_ttc} onChange={e => editItem ? setEditItem({ ...editItem, prix_ttc: e.target.value }) : setNewItem({ ...newItem, prix_ttc: e.target.value })} className="text-sm h-8" /></div>
            <div><label className="text-[10px] text-gray-400 block mb-0.5">Unité</label>
              <select value={editItem ? editItem.unite : newItem.unite} onChange={e => editItem ? setEditItem({ ...editItem, unite: e.target.value }) : setNewItem({ ...newItem, unite: e.target.value })} className="w-full border rounded-lg px-2 py-1.5 text-sm h-8 bg-white">
                {['€/pers', '€', '€/h', '€/btle', '€/vasque', '€/plateau', '€/pièce', '€/baguette', '€/soirée', '€/jour', '€/week-end'].map(u => <option key={u} value={u}>{u}</option>)}
              </select></div>
            <div className="col-span-2"><label className="text-[10px] text-gray-400 block mb-0.5">Description</label>
              <textarea value={editItem ? editItem.desc : newItem.desc} onChange={e => editItem ? setEditItem({ ...editItem, desc: e.target.value }) : setNewItem({ ...newItem, desc: e.target.value })} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm resize-none" /></div>
          </div>
          <div className="flex gap-2">
            <Button onClick={editItem ? handleEdit : handleAdd} disabled={saving} size="sm" className="bg-[#009893] hover:bg-[#007d79] text-white text-xs">
              {saving ? <Loader2 size={12} className="animate-spin mr-1" /> : <Check size={12} className="mr-1" />} {editItem ? 'Modifier' : 'Ajouter'}
            </Button>
            <Button onClick={() => { setShowAdd(false); setEditItem(null); }} variant="outline" size="sm" className="text-xs">Annuler</Button>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {Object.entries(grouped).map(([cat, items]) => (
          <div key={cat}>
            <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 flex items-center gap-2">
              <ChefHat size={14} className="text-[#009893]" />
              {cat}
              <span className="text-[10px] font-normal text-gray-400 lowercase">({items.length} articles)</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {items.map(item => (
                <div key={item.ref} className={`bg-gradient-to-br ${catColors[cat] || 'from-gray-50 to-gray-25 border-gray-200'} border rounded-xl p-3.5 hover:shadow-md transition-all group ${pdfMode && selectedRefs.has(item.ref) ? 'ring-2 ring-[#009893]' : ''}`}
                  data-testid={`cat-item-${item.ref}`}
                  onClick={pdfMode ? () => toggleRef(item.ref) : undefined}
                  style={pdfMode ? { cursor: 'pointer' } : {}}>
                  <div className="flex items-start justify-between gap-2">
                    {pdfMode && (
                      <input type="checkbox" checked={selectedRefs.has(item.ref)} onChange={() => toggleRef(item.ref)}
                        className="mt-0.5 rounded border-gray-300 text-[#009893] shrink-0" onClick={e => e.stopPropagation()} />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-gray-900 leading-tight">{item.nom}</p>
                      <p className="text-[10px] text-gray-500 mt-1 leading-relaxed">{item.desc}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-[#009893] font-mono">{item.prix_ttc}€</p>
                      <p className="text-[9px] text-gray-400">{item.unite}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-dashed border-gray-200/50">
                    <span className="text-[9px] text-gray-400 font-mono">{item.ref}</span>
                    {!pdfMode && (
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => setEditItem({ ...item, prix_ttc: String(item.prix_ttc) })} className="text-[9px] text-[#009893] hover:underline">Modifier</button>
                        <button onClick={() => handleDelete(item.ref)} className="text-[9px] text-red-400 hover:text-red-600">Supprimer</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════
// VEILLE & MARCHÉ
// ═══════════════════════════════════════════════

function VeilleMarche() {
  const [activeSection, setActiveSection] = useState('concurrents');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState({});

  useEffect(() => {
    VEILLE_SECTIONS.forEach(async s => {
      try {
        const res = await fetch(`${API}/api/propositions/veille/${s.id}`);
        const d = await res.json();
        if (d.content) setData(prev => ({ ...prev, [s.id]: d }));
      } catch (e) { console.error(e); }
    });
  }, []);

  const refresh = async (section) => {
    setLoading(prev => ({ ...prev, [section]: true }));
    try {
      const res = await fetch(`${API}/api/propositions/veille/analyse`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ section }),
      });
      const d = await res.json();
      if (res.ok) {
        setData(prev => ({ ...prev, [section]: d }));
        toast.success('Analyse mise à jour');
      } else {
        toast.error(d.detail || 'Erreur IA');
      }
    } catch { toast.error('Erreur réseau'); }
    setLoading(prev => ({ ...prev, [section]: false }));
  };

  const copyContent = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copié');
  };

  const current = data[activeSection];

  return (
    <div className="space-y-4" data-testid="veille-marche">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {VEILLE_SECTIONS.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)} data-testid={`veille-tab-${s.id}`}
            className={`flex items-center gap-2 p-3 rounded-xl border text-left transition-all ${activeSection === s.id ? 'border-[#009893] bg-[#009893]/5 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}>
            <s.icon size={18} className={activeSection === s.id ? 'text-[#009893]' : 'text-gray-400'} />
            <div>
              <p className={`text-xs font-medium ${activeSection === s.id ? 'text-[#009893]' : 'text-gray-700'}`}>{s.label}</p>
              <p className="text-[10px] text-gray-400">{s.desc}</p>
            </div>
          </button>
        ))}
      </div>

      <div className="bg-white border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-gray-900">{VEILLE_SECTIONS.find(s => s.id === activeSection)?.label}</h3>
          <div className="flex items-center gap-2">
            {current?.updated_at && <span className="text-[10px] text-gray-400">Mis à jour le {new Date(current.updated_at).toLocaleDateString('fr-FR')}</span>}
            <Button onClick={() => refresh(activeSection)} disabled={loading[activeSection]} size="sm" className="bg-[#009893] hover:bg-[#007d79] text-white text-xs" data-testid="refresh-veille-btn">
              {loading[activeSection] ? <Loader2 size={12} className="animate-spin mr-1" /> : <Sparkles size={12} className="mr-1" />} Actualiser l'analyse
            </Button>
          </div>
        </div>

        {loading[activeSection] && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-[#009893] mr-2" size={24} />
            <span className="text-sm text-gray-500">L'IA analyse le marché...</span>
          </div>
        )}

        {!loading[activeSection] && current?.content ? (
          <div>
            <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap" style={{ fontFamily: 'Georgia, serif', lineHeight: '1.8', fontSize: '13px' }}>
              {current.content}
            </div>
            <div className="flex gap-2 mt-4 pt-4 border-t">
              <Button onClick={() => copyContent(current.content)} variant="outline" size="sm" className="text-xs">
                <Copy size={12} className="mr-1" /> Copier le texte
              </Button>
            </div>
          </div>
        ) : !loading[activeSection] && (
          <div className="text-center py-16 text-gray-400">
            <Sparkles size={32} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">Aucune analyse disponible</p>
            <p className="text-xs mt-1">Cliquez sur "Actualiser l'analyse" pour lancer l'IA</p>
          </div>
        )}
      </div>
    </div>
  );
}



// ══════ MENUS DE GROUPE ══════


// ══════ RESTAURANT SUB-TABS ══════
function RestaurantSubTabs() {
  const [subTab, setSubTab] = useState('pipeline');

  const SUB_TABS = [
    { id: 'pipeline', label: 'Pipeline', icon: Target },
    { id: 'menus', label: 'Menus', icon: ChefHat },
  ];

  return (
    <div className="space-y-4">
      <div className="flex gap-1 bg-orange-50/50 rounded-lg p-1">
        {SUB_TABS.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} data-testid={`restaurant-subtab-${t.id}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-medium transition-all ${subTab === t.id ? 'bg-white text-[#E07A5F] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>
      {subTab === 'pipeline' && <MenuGroupePipeline typeMenu="restaurant" accentColor="#E07A5F" />}
      {subTab === 'menus' && <MenusGroupeView />}
    </div>
  );
}


// ══════ MENU GROUPE PIPELINE (Kanban) ══════
function MenuGroupePipeline({ typeMenu = 'restaurant', accentColor = '#E07A5F' }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCard, setEditingCard] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/menus-groupe-pipeline?type_menu=${typeMenu}`);
      const data = await r.json();
      setCards(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Erreur chargement pipeline');
    }
    setLoading(false);
  }, [typeMenu]);

  useEffect(() => { load(); }, [load]);

  const changeStatut = async (cardId, newStatut) => {
    try {
      const r = await fetch(`${API}/api/menus-groupe-pipeline/${cardId}/statut`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ statut: newStatut }),
      });
      if (r.ok) {
        toast.success(`Statut → ${STATUT_LABELS[newStatut]}`);
        load();
      } else toast.error('Erreur changement statut');
    } catch { toast.error('Erreur'); }
  };

  const deleteCard = async (cardId) => {
    if (!window.confirm('Supprimer cette opportunite ?')) return;
    try {
      await fetch(`${API}/api/menus-groupe-pipeline/${cardId}`, { method: 'DELETE' });
      toast.success('Carte supprimee');
      load();
    } catch { toast.error('Erreur'); }
  };

  const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);
  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }); } catch { return ''; }
  };

  // KPIs
  const totalEnvoye = cards.reduce((s, c) => s + (c.total || 0), 0);
  const totalAcceptee = cards.filter(c => c.statut === 'acceptee').reduce((s, c) => s + (c.total || 0), 0);
  const taux = cards.length ? Math.round(100 * cards.filter(c => c.statut === 'acceptee').length / cards.length) : 0;

  if (loading) return <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-gray-400" /></div>;

  if (cards.length === 0) {
    return (
      <div className="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center" data-testid="empty-pipeline">
        <Target size={32} className="mx-auto text-gray-300 mb-2" />
        <p className="text-sm font-medium text-gray-700">Aucune opportunite pour l'instant</p>
        <p className="text-[11px] text-gray-400 mt-1">
          Chaque envoi de menu groupe a un client cree automatiquement une carte ici.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* KPIs */}
      <div className="grid grid-cols-4 gap-2">
        <div className="bg-white border border-gray-100 rounded-lg p-3">
          <p className="text-[9px] uppercase tracking-wider text-gray-400 font-bold">Opportunites</p>
          <p className="text-xl font-mono font-bold text-gray-900">{cards.length}</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-lg p-3">
          <p className="text-[9px] uppercase tracking-wider text-gray-400 font-bold">Montant pipeline</p>
          <p className="text-xl font-mono font-bold text-blue-600">{fmt(totalEnvoye)}</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-lg p-3">
          <p className="text-[9px] uppercase tracking-wider text-gray-400 font-bold">CA acceptee</p>
          <p className="text-xl font-mono font-bold text-emerald-600">{fmt(totalAcceptee)}</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-lg p-3">
          <p className="text-[9px] uppercase tracking-wider text-gray-400 font-bold">Taux conversion</p>
          <p className="text-xl font-mono font-bold" style={{ color: accentColor }}>{taux}%</p>
        </div>
      </div>

      {/* Kanban */}
      <div className="flex gap-3 overflow-x-auto pb-4" data-testid="menugroupe-pipeline" style={{ minHeight: '60vh' }}>
        {STATUTS.map(statut => {
          const items = cards.filter(c => c.statut === statut);
          const cc = STATUT_COLORS[statut];
          const colTotal = items.reduce((s, c) => s + (c.total || 0), 0);
          return (
            <div key={statut} className="flex-shrink-0 w-64 bg-gray-50 rounded-xl p-3 space-y-2" data-testid={`mg-pipeline-col-${statut}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${cc.dot}`} />
                  <h3 className={`text-xs font-bold uppercase tracking-wider ${cc.text}`}>{STATUT_LABELS[statut]}</h3>
                </div>
                <span className="text-[10px] font-mono text-gray-400">{items.length}</span>
              </div>
              {colTotal > 0 && <p className="text-[10px] font-mono text-gray-400 -mt-1">{fmt(colTotal)}</p>}

              <div className="space-y-2">
                {items.map(c => {
                  const dateStr = c.date_event ? fmtDate(c.date_event) : '';
                  const lastEmail = c.last_email_at ? fmtDate(c.last_email_at) : '';
                  const nextStatuts = STATUTS.filter(s => s !== statut);
                  return (
                    <div key={c.id} className="bg-white rounded-lg border p-3 shadow-sm hover:shadow-md transition-shadow group" data-testid={`mg-card-${c.id}`}>
                      <div className="flex items-start justify-between gap-1 mb-1.5">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-gray-900 truncate">{c.client_nom || c.client_email}</p>
                          <p className="text-[10px] text-gray-500 truncate">{c.menu_nom}</p>
                        </div>
                        <button onClick={() => deleteCard(c.id)} className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`mg-delete-${c.id}`}>
                          <Trash2 size={11} />
                        </button>
                      </div>
                      <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1.5">
                        <span className="flex items-center gap-0.5"><Users size={9} /> {c.nb_personnes} pers</span>
                        {dateStr && <span className="flex items-center gap-0.5"><Calendar size={9} /> {dateStr}</span>}
                      </div>
                      <div className="flex items-center justify-between border-t pt-1.5">
                        <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                          {c.nb_emails > 0 && <><Mail size={9} /> {c.nb_emails}{lastEmail ? ` · ${lastEmail}` : ''}</>}
                        </span>
                        <span className="text-sm font-mono font-bold text-[#009893]">{fmt(c.total)}</span>
                      </div>
                      {c.notes && <p className="text-[10px] text-gray-400 mt-1.5 italic line-clamp-2">{c.notes}</p>}
                      <div className="flex flex-wrap gap-1 mt-2 pt-1.5 border-t opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => setEditingCard(c)} className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 hover:bg-gray-200" data-testid={`mg-edit-${c.id}`}>
                          Notes
                        </button>
                        {nextStatuts.map(ns => (
                          <button key={ns} onClick={() => changeStatut(c.id, ns)} data-testid={`mg-move-${c.id}-${ns}`}
                            className={`text-[9px] px-1.5 py-0.5 rounded ${STATUT_COLORS[ns].bg} ${STATUT_COLORS[ns].text} hover:opacity-80`}>
                            {STATUT_LABELS[ns]}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {editingCard && (
        <MenuGroupePipelineEditModal card={editingCard} onClose={() => setEditingCard(null)} onSaved={() => { setEditingCard(null); load(); }} />
      )}
    </div>
  );
}


function MenuGroupePipelineEditModal({ card, onClose, onSaved }) {
  const [clientNom, setClientNom] = useState(card.client_nom || '');
  const [clientEmail, setClientEmail] = useState(card.client_email || '');
  const [nbPers, setNbPers] = useState(card.nb_personnes || 0);
  const [dateEvent, setDateEvent] = useState(card.date_event || '');
  const [notes, setNotes] = useState(card.notes || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/menus-groupe-pipeline/${card.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_nom: clientNom, client_email: clientEmail,
          nb_personnes: Number(nbPers), date_event: dateEvent, notes,
          total: Number(nbPers) * (card.menu_prix || 0),
        }),
      });
      if (r.ok) { toast.success('Carte mise a jour'); onSaved(); }
      else toast.error('Erreur enregistrement');
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-xl p-5 max-w-md w-full space-y-3" onClick={e => e.stopPropagation()} data-testid="mg-edit-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-900">Modifier l'opportunite</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>
        <div className="space-y-2">
          <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold block">Client</label>
          <Input value={clientNom} onChange={e => setClientNom(e.target.value)} placeholder="Nom du client" data-testid="mg-edit-nom" />
          <Input value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder="Email" data-testid="mg-edit-email" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold block mb-1">Personnes</label>
              <Input type="number" value={nbPers} onChange={e => setNbPers(e.target.value)} data-testid="mg-edit-nbpers" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold block mb-1">Date event</label>
              <Input type="date" value={dateEvent} onChange={e => setDateEvent(e.target.value)} data-testid="mg-edit-date" />
            </div>
          </div>
          <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold block">Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Ajouter des notes..."
            className="w-full text-xs border rounded-md px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-[#E07A5F]" data-testid="mg-edit-notes" />
        </div>
        <div className="flex gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} className="flex-1 text-xs h-9">Annuler</Button>
          <Button onClick={save} disabled={saving} className="flex-1 bg-[#009893] hover:bg-[#007d79] text-white text-xs h-9" data-testid="mg-edit-save">
            {saving ? <Loader2 size={12} className="animate-spin mr-1" /> : <Check size={12} className="mr-1" />}
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
}


// ══════ TRAITEUR SUB-TABS ══════
function TraiteurSubTabs({ propositions, catalogue, typesEvenement, stats, onSelect, onRefresh }) {
  const [subTab, setSubTab] = useState('pipeline');

  const SUB_TABS = [
    { id: 'pipeline', label: 'Pipeline', icon: Target },
    { id: 'catalogue', label: 'Catalogue', icon: ChefHat },
    { id: 'veille', label: 'Veille & Marche', icon: TrendingUp },
  ];

  return (
    <div className="space-y-4">
      <div className="flex gap-1 bg-purple-50/50 rounded-lg p-1">
        {SUB_TABS.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-medium transition-all ${subTab === t.id ? 'bg-white text-[#7C3AED] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>
      {subTab === 'pipeline' && <PropPipeline propositions={propositions} onSelect={onSelect} onRefresh={onRefresh} />}
      {subTab === 'catalogue' && <CatalogueView catalogue={catalogue} onRefresh={onRefresh} />}
      {subTab === 'veille' && <VeilleMarche />}
    </div>
  );
}


function MenusGroupeView() {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [sendModal, setSendModal] = useState(null);
  const [sending, setSending] = useState(false);
  const [generatingMail, setGeneratingMail] = useState(false);
  const [iaModifying, setIaModifying] = useState(null);
  const [iaInstruction, setIaInstruction] = useState('');
  const [iaLoading, setIaLoading] = useState(false);
  const [emailHistory, setEmailHistory] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  const [optionsCatalogue, setOptionsCatalogue] = useState([]);
  const [form, setForm] = useState({ nom: '', prix: 0, min_personnes: 10, type_menu: 'restaurant', entree: '', plat: '', dessert: '', inclus: '', notes: '', options: [] });

  const MAIL_TYPES = [
    { id: 'premier_contact', label: 'Premier contact', color: 'bg-blue-500' },
    { id: 'relance_j5', label: 'Relance J+5', color: 'bg-amber-500' },
    { id: 'relance_j10', label: 'Relance J+10', color: 'bg-orange-500' },
    { id: 'confirmation', label: 'Confirmation', color: 'bg-emerald-500' },
    { id: 'remerciement', label: 'Remerciement', color: 'bg-purple-500' },
  ];

  const load = useCallback(async () => {
    try {
      const [r, oc] = await Promise.all([
        fetch(`${API}/api/propositions/menus-groupe`),
        fetch(`${API}/api/propositions/options-catalogue`),
      ]);
      setMenus(await r.json());
      setOptionsCatalogue(await oc.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveMenu = async () => {
    if (!form.nom.trim() || !form.plat.trim()) { toast.error('Nom et plat sont requis'); return; }
    try {
      const r = await fetch(`${API}/api/propositions/menus-groupe`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editing ? { ...form, id: editing } : form)
      });
      if (r.ok) { toast.success(editing ? 'Menu mis a jour' : 'Menu cree'); load(); resetForm(); }
    } catch { toast.error('Erreur'); }
  };

  const deleteMenu = async (id) => {
    if (!window.confirm('Supprimer ce menu ?')) return;
    await fetch(`${API}/api/propositions/menus-groupe/${id}`, { method: 'DELETE' });
    toast.success('Supprime'); load();
  };

  const toggleMenu = async (id) => {
    try {
      const r = await fetch(`${API}/api/propositions/menus-groupe/${id}/toggle`, { method: 'PUT' });
      const data = await r.json();
      if (data.success) {
        toast.success(data.actif ? 'Menu active' : 'Menu desactive');
        load();
      }
    } catch { toast.error('Erreur'); }
  };

  const generateMail = async (mailType) => {
    if (!sendModal?.menu) return;
    setGeneratingMail(true);
    try {
      const r = await fetch(`${API}/api/propositions/menus-groupe/${sendModal.menu.id}/generate-mail`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: mailType,
          client_nom: sendModal.clientNom || '',
          nb_personnes: sendModal.nbPers || sendModal.menu.min_personnes,
          date_event: sendModal.dateEvent || '',
          instruction: sendModal.iaInstruction || '',
        })
      });
      const data = await r.json();
      if (data.subject && data.body) {
        setSendModal(p => ({ ...p, message: data.body, subject: data.subject, mailType }));
        toast.success('Email genere par l\'IA');
      } else {
        toast.error(data.detail || 'Erreur IA');
      }
    } catch { toast.error('Erreur IA'); }
    setGeneratingMail(false);
  };

  const sendMenu = async () => {
    if (!sendModal?.to) { toast.error('Email requis'); return; }
    setSending(true);
    try {
      const r = await fetch(`${API}/api/propositions/menus-groupe/${sendModal.menu.id}/send-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: sendModal.to,
          nb_personnes: sendModal.nbPers || sendModal.menu.min_personnes,
          message: sendModal.message || '',
          client_nom: sendModal.clientNom || '',
          subject: sendModal.subject || '',
          mail_type: sendModal.mailType || 'premier_contact',
        })
      });
      const data = await r.json();
      if (data.success) { toast.success(`Menu envoye ! Total: ${data.total}€`); setSendModal(null); load(); }
      else toast.error(data.detail || 'Erreur');
    } catch { toast.error('Erreur envoi'); }
    setSending(false);
  };

  const iaModifyMenu = async (menuId) => {
    if (!iaInstruction.trim()) { toast.error('Donnez une instruction'); return; }
    setIaLoading(true);
    try {
      const r = await fetch(`${API}/api/propositions/menus-groupe/${menuId}/ia-modify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instruction: iaInstruction })
      });
      const data = await r.json();
      if (data.suggestion) {
        setEditing(menuId);
        setForm({
          nom: data.suggestion.nom || '',
          prix: data.suggestion.prix || 0,
          min_personnes: data.suggestion.min_personnes || 10,
          entree: data.suggestion.entree || '',
          plat: data.suggestion.plat || '',
          dessert: data.suggestion.dessert || '',
          inclus: data.suggestion.inclus || '',
          notes: data.suggestion.notes || '',
        });
        setIaModifying(null);
        setIaInstruction('');
        toast.success('Menu modifie par l\'IA — verifiez et sauvegardez');
      } else {
        toast.error('L\'IA n\'a pas pu generer de suggestion');
      }
    } catch { toast.error('Erreur IA'); }
    setIaLoading(false);
  };

  const loadEmailHistory = async (menuId) => {
    try {
      const r = await fetch(`${API}/api/propositions/menus-groupe/${menuId}/emails`);
      const data = await r.json();
      setEmailHistory({ menuId, emails: data });
    } catch { toast.error('Erreur'); }
  };

  const editMenu = (m) => {
    setEditing(m.id);
    setForm({ nom: m.nom, prix: m.prix, min_personnes: m.min_personnes, type_menu: m.type_menu || 'restaurant', entree: m.entree || '', plat: m.plat, dessert: m.dessert || '', inclus: m.inclus || '', notes: m.notes || '', options: m.options || [] });
  };

  const resetForm = () => {
    setEditing(null);
    setForm({ nom: '', prix: 0, min_personnes: 10, type_menu: 'restaurant', entree: '', plat: '', dessert: '', inclus: '', notes: '', options: [] });
  };

  const filteredMenus = menus.filter(m => {
    if (filterType !== 'all' && (m.type_menu || 'restaurant') !== filterType) return false;
    if (!showInactive && m.actif === false) return false;
    return true;
  });

  const restaurantCount = menus.filter(m => (m.type_menu || 'restaurant') === 'restaurant' && m.actif !== false).length;
  const traiteurCount = menus.filter(m => m.type_menu === 'traiteur' && m.actif !== false).length;
  const inactiveCount = menus.filter(m => m.actif === false).length;

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-[#009893]" /></div>;

  return (
    <div className="space-y-4" data-testid="menus-groupe-view">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-bold text-gray-900">Menus de Groupe</h2>
          <p className="text-[10px] text-gray-400">Creez, modifiez avec l'IA, et envoyez des menus pour groupes</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setFilterType('all')}
          className={`px-3 py-1.5 rounded-lg text-[10px] font-medium transition-all ${filterType === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
          Tous ({menus.filter(m => m.actif !== false).length})
        </button>
        <button onClick={() => setFilterType('restaurant')}
          className={`px-3 py-1.5 rounded-lg text-[10px] font-medium transition-all ${filterType === 'restaurant' ? 'bg-[#E07A5F] text-white' : 'bg-[#E07A5F]/10 text-[#E07A5F] hover:bg-[#E07A5F]/20'}`}>
          Restaurant ({restaurantCount})
        </button>
        <button onClick={() => setFilterType('traiteur')}
          className={`px-3 py-1.5 rounded-lg text-[10px] font-medium transition-all ${filterType === 'traiteur' ? 'bg-[#7C3AED] text-white' : 'bg-[#7C3AED]/10 text-[#7C3AED] hover:bg-[#7C3AED]/20'}`}>
          Traiteur ({traiteurCount})
        </button>
        {inactiveCount > 0 && (
          <button onClick={() => setShowInactive(!showInactive)}
            className={`px-3 py-1.5 rounded-lg text-[10px] font-medium transition-all ${showInactive ? 'bg-gray-400 text-white' : 'bg-gray-100 text-gray-400'}`}>
            {showInactive ? 'Masquer' : 'Voir'} inactifs ({inactiveCount})
          </button>
        )}
      </div>

      {/* Existing menus grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filteredMenus.map(m => {
          const isActive = m.actif !== false;
          const isRestaurant = (m.type_menu || 'restaurant') === 'restaurant';
          const typeColor = isRestaurant ? '#E07A5F' : '#7C3AED';
          const typeLabel = isRestaurant ? 'Restaurant' : 'Traiteur';

          return (
          <div key={m.id} className={`bg-white rounded-xl border shadow-sm p-4 transition-all group ${isActive ? 'border-gray-100 hover:shadow-md' : 'border-gray-200 opacity-50'}`} data-testid={`menu-groupe-${m.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-gray-900 text-sm">{m.nom}</h3>
                  <span className="text-[8px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider" style={{ background: typeColor + '15', color: typeColor }}>{typeLabel}</span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-lg font-bold text-[#009893]">{m.prix}€</span>
                  <span className="text-[10px] text-gray-400">/ pers.</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">min. {m.min_personnes} pers.</span>
                  {m.emails_envoyes?.length > 0 && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600">{m.emails_envoyes.length} email{m.emails_envoyes.length > 1 ? 's' : ''}</span>
                  )}
                </div>
              </div>
              {/* Toggle actif/inactif */}
              <button onClick={() => toggleMenu(m.id)} title={isActive ? 'Desactiver' : 'Activer'}
                className={`w-10 h-5 rounded-full relative transition-colors ${isActive ? 'bg-[#009893]' : 'bg-gray-300'}`}
                data-testid={`toggle-menu-${m.id}`}>
                <div className={`w-4 h-4 rounded-full bg-white shadow absolute top-0.5 transition-all ${isActive ? 'left-5' : 'left-0.5'}`} />
              </button>
            </div>

            <div className="space-y-2 text-xs text-gray-600">
              {m.entree && <div><span className="text-[9px] font-bold uppercase tracking-wider text-[#C9A84C]">Entree</span><p className="whitespace-pre-line">{m.entree}</p></div>}
              <div><span className="text-[9px] font-bold uppercase tracking-wider text-[#C9A84C]">Plat</span><p className="whitespace-pre-line">{m.plat}</p></div>
              {m.dessert && <div><span className="text-[9px] font-bold uppercase tracking-wider text-[#C9A84C]">Dessert</span><p className="whitespace-pre-line">{m.dessert}</p></div>}
              {m.inclus && <p className="text-[10px] italic text-gray-400 border-t pt-1.5 mt-1.5">{m.inclus}</p>}
              {m.options?.length > 0 && (
                <div className="border-t pt-1.5 mt-1.5">
                  <span className="text-[9px] font-bold uppercase tracking-wider text-amber-500">Options</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {m.options.map((opt, oi) => (
                      <span key={oi} className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">{opt.nom} {opt.prix}€</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-1.5 mt-3 pt-2 border-t border-gray-50 flex-wrap">
              <button onClick={() => setSendModal({ menu: m, to: '', nbPers: m.min_personnes, message: `Bonjour,\n\nVeuillez trouver ci-joint notre proposition de menu "${m.nom}" a ${m.prix}€ par personne.\n\nN'hesitez pas a nous contacter pour toute question.\n\nLe Mas Restaurant Traiteur`, clientNom: '', subject: `Proposition Menu — ${m.nom} — Le Mas`, mailType: 'premier_contact', dateEvent: '', iaInstruction: '' })}
                className="flex-1 py-1.5 rounded-lg bg-[#009893] text-white text-[10px] font-medium hover:bg-[#007d79] flex items-center justify-center gap-1" data-testid={`send-menu-${m.id}`}>
                <Send size={10} /> Envoyer (IA)
              </button>
              <button onClick={() => window.open(`${API}/api/propositions/menus-groupe/${m.id}/pdf`, '_blank')}
                className="px-3 py-1.5 rounded-lg bg-gray-50 text-gray-600 text-[10px] hover:bg-gray-100 flex items-center gap-1" data-testid={`pdf-menu-${m.id}`}>
                <FileText size={10} /> PDF
              </button>
              <button onClick={() => setIaModifying(iaModifying === m.id ? null : m.id)}
                className="px-3 py-1.5 rounded-lg bg-purple-50 text-purple-600 text-[10px] hover:bg-purple-100 flex items-center gap-1" data-testid={`ia-modify-${m.id}`}>
                <Sparkles size={10} /> IA
              </button>
              <button onClick={() => editMenu(m)}
                className="px-3 py-1.5 rounded-lg bg-gray-50 text-gray-500 text-[10px] hover:bg-gray-100">Modifier</button>
              {m.emails_envoyes?.length > 0 && (
                <button onClick={() => loadEmailHistory(m.id)}
                  className="px-2 py-1.5 rounded-lg bg-blue-50 text-blue-500 text-[10px] hover:bg-blue-100 flex items-center gap-1">
                  <Mail size={10} /> Suivi
                </button>
              )}
              <button onClick={() => deleteMenu(m.id)}
                className="px-2 py-1.5 rounded-lg bg-red-50 text-red-400 text-[10px] hover:bg-red-100"><Trash2 size={10} /></button>
            </div>

            {/* IA Modify inline */}
            {iaModifying === m.id && (
              <div className="mt-3 p-3 bg-purple-50 border border-purple-100 rounded-lg space-y-2">
                <p className="text-[10px] font-bold text-purple-700">Modifier avec l'IA</p>
                <textarea value={iaInstruction} onChange={e => setIaInstruction(e.target.value)} rows={2}
                  placeholder="Ex: Remplace le dessert par quelque chose de plus estival, ajoute du poisson..."
                  className="w-full text-xs border border-purple-200 rounded-md px-2 py-1.5 resize-none" data-testid="ia-instruction" />
                <div className="flex gap-2">
                  <Button onClick={() => iaModifyMenu(m.id)} disabled={iaLoading} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white text-[10px]" data-testid="ia-modify-btn">
                    {iaLoading ? <Loader2 size={10} className="animate-spin mr-1" /> : <Sparkles size={10} className="mr-1" />}
                    Generer
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setIaModifying(null); setIaInstruction(''); }} className="text-[10px]">Annuler</Button>
                </div>
              </div>
            )}

            {m.notes && <p className="text-[9px] text-gray-400 mt-2">{m.notes}</p>}
          </div>
          );
        })}
      </div>

      {/* Create/Edit form */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3">
        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">{editing ? 'Modifier le menu' : 'Nouveau menu de groupe'}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="col-span-2"><label className="text-[10px] text-gray-400 block mb-0.5">Nom du menu *</label>
            <Input value={form.nom} onChange={e => setForm(p => ({ ...p, nom: e.target.value }))} placeholder="Menu Groupe Classique" className="text-xs h-8" data-testid="mg-nom" /></div>
          <div><label className="text-[10px] text-gray-400 block mb-0.5">Prix/pers.</label>
            <Input type="number" value={form.prix} onChange={e => setForm(p => ({ ...p, prix: Number(e.target.value) }))} className="text-xs h-8" data-testid="mg-prix" /></div>
          <div><label className="text-[10px] text-gray-400 block mb-0.5">Type</label>
            <select value={form.type_menu} onChange={e => setForm(p => ({ ...p, type_menu: e.target.value }))} className="w-full text-xs h-8 border rounded-md px-2" data-testid="mg-type">
              <option value="restaurant">Restaurant</option>
              <option value="traiteur">Traiteur</option>
            </select></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div><label className="text-[10px] text-gray-400 block mb-0.5">Entree</label>
            <textarea value={form.entree} onChange={e => setForm(p => ({ ...p, entree: e.target.value }))} rows={3} placeholder="Salade du Mas ou..." className="w-full text-xs border rounded-md px-2 py-1.5 resize-none" /></div>
          <div><label className="text-[10px] text-gray-400 block mb-0.5">Plat *</label>
            <textarea value={form.plat} onChange={e => setForm(p => ({ ...p, plat: e.target.value }))} rows={3} placeholder="Magret de canard..." className="w-full text-xs border rounded-md px-2 py-1.5 resize-none" data-testid="mg-plat" /></div>
          <div><label className="text-[10px] text-gray-400 block mb-0.5">Dessert</label>
            <textarea value={form.dessert} onChange={e => setForm(p => ({ ...p, dessert: e.target.value }))} rows={3} placeholder="Creme catalane..." className="w-full text-xs border rounded-md px-2 py-1.5 resize-none" /></div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-[10px] text-gray-400 block mb-0.5">Inclus (boissons, etc.)</label>
            <Input value={form.inclus} onChange={e => setForm(p => ({ ...p, inclus: e.target.value }))} placeholder="Pain, eau, 1/4 vin" className="text-xs h-8" /></div>
          <div><label className="text-[10px] text-gray-400 block mb-0.5">Notes</label>
            <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Ideal pour..." className="text-xs h-8" /></div>
        </div>

        {/* Options selector from catalogue */}
        <div>
          <label className="text-[10px] text-gray-400 block mb-1.5">Options supplementaires</label>
          {form.options.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {form.options.map((opt, i) => (
                <span key={i} className="text-[9px] px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1">
                  {opt.nom} — {opt.prix}€
                  <button onClick={() => setForm(p => ({ ...p, options: p.options.filter((_, j) => j !== i) }))} className="text-amber-400 hover:text-red-500"><X size={10} /></button>
                </span>
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 max-h-40 overflow-y-auto p-2 bg-gray-50 rounded-lg border">
            {optionsCatalogue.map((cat) => (
              <div key={cat.categorie}>
                <p className="text-[8px] font-bold text-gray-500 uppercase tracking-wider mb-0.5">{cat.categorie}</p>
                {cat.items?.map((item, j) => {
                  const isSelected = form.options.some(o => o.nom === item.nom);
                  return (
                    <button key={j} onClick={() => {
                      if (isSelected) {
                        setForm(p => ({ ...p, options: p.options.filter(o => o.nom !== item.nom) }));
                      } else {
                        setForm(p => ({ ...p, options: [...p.options, { nom: item.nom, prix: item.prix }] }));
                      }
                    }}
                      className={`w-full text-left text-[9px] px-1.5 py-0.5 rounded mb-0.5 transition-all ${isSelected ? 'bg-amber-100 text-amber-800 font-medium' : 'text-gray-600 hover:bg-amber-50'}`}>
                      {item.nom} <span className="text-gray-400">{item.prix}€</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={saveMenu} className="bg-[#009893] hover:bg-[#007d79] text-white text-xs" data-testid="mg-save-btn">
            <Check size={12} className="mr-1" /> {editing ? 'Mettre a jour' : 'Sauvegarder'}
          </Button>
          {editing && <Button variant="ghost" onClick={resetForm} className="text-xs">Annuler</Button>}
        </div>
      </div>

      {/* Send Email Modal with IA */}
      {sendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setSendModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-5 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="send-menu-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-900 text-sm">Envoyer "{sendModal.menu.nom}"</h3>
              <button onClick={() => setSendModal(null)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
            </div>
            <div className="p-3 rounded-lg bg-[#009893]/5 border border-[#009893]/20">
              <p className="text-xs font-medium text-[#009893]">{sendModal.menu.nom} — {sendModal.menu.prix}€/pers.</p>
              <div className="text-[9px] text-gray-500 mt-1 space-y-0.5">
                {sendModal.menu.entree && <p>Entree: {sendModal.menu.entree.split('\n')[0]}</p>}
                <p>Plat: {sendModal.menu.plat?.split('\n')[0]}</p>
                {sendModal.menu.dessert && <p>Dessert: {sendModal.menu.dessert.split('\n')[0]}</p>}
              </div>
            </div>

            {/* Client info */}
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[10px] text-gray-400 block mb-0.5">Email du client *</label>
                <Input value={sendModal.to} onChange={e => setSendModal(p => ({ ...p, to: e.target.value }))} placeholder="client@email.com" className="text-xs h-8" data-testid="mg-send-email" autoFocus /></div>
              <div><label className="text-[10px] text-gray-400 block mb-0.5">Nom du client</label>
                <Input value={sendModal.clientNom} onChange={e => setSendModal(p => ({ ...p, clientNom: e.target.value }))} placeholder="Societe / Nom" className="text-xs h-8" /></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><label className="text-[10px] text-gray-400 block mb-0.5">Nb personnes</label>
                <Input type="number" value={sendModal.nbPers} onChange={e => setSendModal(p => ({ ...p, nbPers: Number(e.target.value) }))} className="text-xs h-8" data-testid="mg-send-nbpers" /></div>
              <div><label className="text-[10px] text-gray-400 block mb-0.5">Date evenement</label>
                <Input type="date" value={sendModal.dateEvent} onChange={e => setSendModal(p => ({ ...p, dateEvent: e.target.value }))} className="text-xs h-8" /></div>
              <div className="flex items-end pb-1">
                <p className="text-lg font-bold text-[#009893]">{sendModal.menu.prix * (sendModal.nbPers || 0)}€ <span className="text-[10px] text-gray-400 font-normal">total</span></p>
              </div>
            </div>

            {/* IA Instructions + Mail Type buttons */}
            <div>
              <p className="text-[10px] text-gray-400 mb-1.5">Vos indications pour l'IA</p>
              <input value={sendModal.iaInstruction} onChange={e => setSendModal(p => ({ ...p, iaInstruction: e.target.value }))}
                placeholder="Ex: ton festif, parle de la vue terrasse, mentionne le parking gratuit..."
                className="w-full mb-2 text-xs border border-purple-200 bg-purple-50 rounded-lg px-3 py-2 text-purple-800 placeholder:text-purple-300 focus:outline-none focus:ring-1 focus:ring-purple-400"
                data-testid="mg-ia-instruction" />
              <p className="text-[10px] text-gray-400 mb-1.5">Type d'email — cliquez pour generer avec l'IA</p>
              <div className="flex gap-1.5 flex-wrap">
                {MAIL_TYPES.map(mt => (
                  <button key={mt.id} onClick={() => generateMail(mt.id)} disabled={generatingMail}
                    className={`px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-all flex items-center gap-1 ${sendModal.mailType === mt.id ? `${mt.color} text-white` : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    data-testid={`mail-type-${mt.id}`}>
                    {generatingMail && sendModal.mailType === mt.id ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                    {mt.label}
                  </button>
                ))}
              </div>
            </div>

            {generatingMail && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="animate-spin text-[#009893] mr-2" size={16} />
                <span className="text-xs text-gray-500">L'IA redige votre email...</span>
              </div>
            )}

            {/* Subject */}
            {sendModal.subject && (
              <div><label className="text-[10px] text-gray-400 block mb-0.5">Objet</label>
                <Input value={sendModal.subject} onChange={e => setSendModal(p => ({ ...p, subject: e.target.value }))} className="text-xs h-8" /></div>
            )}

            {/* Message body */}
            <div><label className="text-[10px] text-gray-400 block mb-0.5">Message</label>
              <textarea value={sendModal.message} onChange={e => setSendModal(p => ({ ...p, message: e.target.value }))} rows={sendModal.message ? 8 : 3}
                placeholder="Cliquez sur un type d'email ci-dessus pour generer le message avec l'IA..."
                className="w-full text-xs border rounded-md px-2 py-1.5 resize-none" style={{ fontFamily: 'Georgia, serif', lineHeight: '1.7' }} data-testid="mg-send-message" /></div>

            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setSendModal(null)} className="flex-1 text-xs">Annuler</Button>
              <Button onClick={sendMenu} disabled={sending} className="flex-1 bg-[#009893] hover:bg-[#007d79] text-white text-xs" data-testid="mg-send-btn">
                {sending ? <Loader2 size={12} className="animate-spin mr-1" /> : <Send size={12} className="mr-1" />}
                Envoyer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Email History Modal */}
      {emailHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setEmailHistory(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()} data-testid="email-history-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-900 text-sm">Suivi emails</h3>
              <button onClick={() => setEmailHistory(null)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
            </div>
            {emailHistory.emails.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">Aucun email envoye</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {emailHistory.emails.map((e, i) => {
                  const typeLabel = MAIL_TYPES.find(mt => mt.id === e.type)?.label || e.type;
                  const typeColor = MAIL_TYPES.find(mt => mt.id === e.type)?.color || 'bg-gray-400';
                  return (
                    <div key={i} className="flex items-start gap-3 bg-gray-50 rounded-lg px-3 py-2">
                      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${typeColor}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold text-gray-700">{typeLabel}</span>
                          {e.client_nom && <span className="text-[10px] text-gray-400">— {e.client_nom}</span>}
                        </div>
                        <p className="text-[10px] text-gray-500 truncate">{e.to}</p>
                        <p className="text-[9px] text-gray-400">{e.date ? new Date(e.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* IA Assistant - Propositions de plats */}
      <IAProposerPlats menus={menus} onRefresh={load} />
    </div>
  );
}

// ══════ IA PROPOSER PLATS ══════
function IAProposerPlats({ menus, onRefresh }) {
  const [instruction, setInstruction] = useState('');
  const [section, setSection] = useState('all');
  const [typeMenu, setTypeMenu] = useState('restaurant');
  const [loading, setLoading] = useState(false);
  const [propositions, setPropositions] = useState([]);
  const [adding, setAdding] = useState(null);

  const SECTIONS = [
    { id: 'all', label: 'Tout' },
    { id: 'entree', label: 'Entrees' },
    { id: 'plat', label: 'Plats' },
    { id: 'dessert', label: 'Desserts' },
  ];

  const generate = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/propositions/menus-groupe/ia-proposer`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instruction: instruction || 'Propose des plats de saison', section, type_menu: typeMenu })
      });
      const data = await r.json();
      if (data.propositions?.length) {
        setPropositions(data.propositions);
        toast.success(`${data.propositions.length} propositions generees`);
      } else {
        toast.error('Aucune proposition');
      }
    } catch { toast.error('Erreur IA'); }
    setLoading(false);
  };

  const addToMenu = async (menuId, sectionName, nom) => {
    setAdding(`${menuId}-${nom}`);
    try {
      const r = await fetch(`${API}/api/propositions/menus-groupe/${menuId}/add-item`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ section: sectionName, nom })
      });
      const data = await r.json();
      if (data.success) {
        toast.success(`"${nom}" ajoute au menu`);
        onRefresh();
      }
    } catch { toast.error('Erreur'); }
    setAdding(null);
  };

  const activeMenus = menus.filter(m => m.actif !== false);
  const sectionLabel = { entree: 'Entree', plat: 'Plat', dessert: 'Dessert' };

  return (
    <div className="bg-white rounded-xl border border-purple-100 shadow-sm p-4 space-y-3" data-testid="ia-proposer-plats">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
          <Sparkles size={16} className="text-purple-500" />
        </div>
        <div>
          <h3 className="text-xs font-bold text-gray-900">IA Chef — Propositions de plats</h3>
          <p className="text-[9px] text-gray-400">L'IA propose des plats que vous pouvez ajouter directement dans vos menus</p>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <textarea value={instruction} onChange={e => setInstruction(e.target.value)} rows={2}
            placeholder="Ex: Propose des plats estivaux, un menu poisson, des entrees vegetariennes..."
            className="w-full text-xs border border-purple-200 rounded-lg px-3 py-2 resize-none focus:ring-1 focus:ring-purple-400" data-testid="ia-propose-input" />
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex gap-1">
            {SECTIONS.map(s => (
              <button key={s.id} onClick={() => setSection(s.id)}
                className={`px-2 py-1 rounded text-[9px] font-medium ${section === s.id ? 'bg-purple-500 text-white' : 'bg-purple-50 text-purple-500'}`}>
                {s.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1">
            <button onClick={() => setTypeMenu('restaurant')}
              className={`flex-1 px-2 py-1 rounded text-[9px] font-medium ${typeMenu === 'restaurant' ? 'bg-[#E07A5F] text-white' : 'bg-[#E07A5F]/10 text-[#E07A5F]'}`}>
              Restaurant
            </button>
            <button onClick={() => setTypeMenu('traiteur')}
              className={`flex-1 px-2 py-1 rounded text-[9px] font-medium ${typeMenu === 'traiteur' ? 'bg-[#7C3AED] text-white' : 'bg-[#7C3AED]/10 text-[#7C3AED]'}`}>
              Traiteur
            </button>
          </div>
        </div>
        <Button onClick={generate} disabled={loading} className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-auto self-stretch" data-testid="ia-propose-btn">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
        </Button>
      </div>

      {/* Propositions */}
      {propositions.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] text-purple-500 font-bold uppercase tracking-wider">{propositions.length} propositions</p>
          {propositions.map((prop, i) => (
            <div key={i} className="flex items-start gap-3 p-3 bg-purple-50/50 rounded-lg border border-purple-100">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[8px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-600 font-bold uppercase">{sectionLabel[prop.section] || prop.section}</span>
                  <span className="text-xs font-bold text-gray-800">{prop.nom}</span>
                </div>
                {prop.description && <p className="text-[10px] text-gray-500 mt-0.5">{prop.description}</p>}
              </div>
              {/* Add to menu dropdown */}
              <div className="flex gap-1 flex-wrap shrink-0">
                {activeMenus.map(m => (
                  <button key={m.id} onClick={() => addToMenu(m.id, prop.section, prop.nom)}
                    disabled={adding === `${m.id}-${prop.nom}`}
                    className="px-2 py-1 rounded text-[8px] bg-white border border-gray-200 text-gray-600 hover:bg-[#009893] hover:text-white hover:border-[#009893] transition-all"
                    title={`Ajouter a "${m.nom}"`}>
                    {adding === `${m.id}-${prop.nom}` ? <Loader2 size={8} className="animate-spin" /> : `+ ${m.nom.split(' ').pop()}`}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function SuiviEmailsView() {
  const [emails, setEmails] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const MAIL_TYPE_LABELS = {
    premier_contact: { label: 'Premier contact', color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-50' },
    relance_j5: { label: 'Relance J+5', color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50' },
    relance_j10: { label: 'Relance J+10', color: 'bg-orange-500', textColor: 'text-orange-700', bgLight: 'bg-orange-50' },
    confirmation: { label: 'Confirmation', color: 'bg-emerald-500', textColor: 'text-emerald-700', bgLight: 'bg-emerald-50' },
    remerciement: { label: 'Remerciement', color: 'bg-purple-500', textColor: 'text-purple-700', bgLight: 'bg-purple-50' },
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [emailsRes, menusRes] = await Promise.all([
          fetch(`${API}/api/crm/email-logs?source=menu_groupe&limit=200`),
          fetch(`${API}/api/propositions/menus-groupe`),
        ]);
        setEmails(await emailsRes.json());
        setMenus(await menusRes.json());
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  // Group emails by client (to address)
  const clientGroups = {};
  emails.forEach(e => {
    const key = e.to || 'inconnu';
    if (!clientGroups[key]) {
      clientGroups[key] = { to: e.to, client_nom: e.prospect_nom || '', emails: [] };
    }
    if (e.prospect_nom && !clientGroups[key].client_nom) clientGroups[key].client_nom = e.prospect_nom;
    clientGroups[key].emails.push(e);
  });

  const clients = Object.values(clientGroups).sort((a, b) => {
    const lastA = a.emails[0]?.date || '';
    const lastB = b.emails[0]?.date || '';
    return lastB.localeCompare(lastA);
  });

  const filtered = filter
    ? clients.filter(c => c.to.toLowerCase().includes(filter.toLowerCase()) || c.client_nom.toLowerCase().includes(filter.toLowerCase()))
    : clients;

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-[#009893]" /></div>;

  return (
    <div className="space-y-4" data-testid="suivi-emails-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold text-gray-900">Suivi des Emails — Menus Groupe</h2>
          <p className="text-[10px] text-gray-400">{emails.length} email{emails.length > 1 ? 's' : ''} envoye{emails.length > 1 ? 's' : ''} — {clients.length} client{clients.length > 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {Object.entries(MAIL_TYPE_LABELS).map(([type, config]) => {
          const count = emails.filter(e => e.subject?.toLowerCase().includes(type.replace('_', ' ')) || e.body?.toLowerCase().includes(type)).length;
          // Better: count by checking email history in menus
          const typeCount = emails.filter(e => {
            const menu = menus.find(m => m.emails_envoyes?.some(me => me.to === e.to && me.type === type));
            return menu !== undefined;
          }).length || emails.filter(e => e.subject?.toLowerCase().includes(config.label.toLowerCase().split(' ')[0])).length;
          return (
            <div key={type} className={`${config.bgLight} rounded-xl p-3 text-center border`}>
              <p className={`text-xl font-bold ${config.textColor}`}>{typeCount || 0}</p>
              <p className="text-[9px] text-gray-500 uppercase tracking-wider">{config.label}</p>
            </div>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Rechercher par client ou email..." className="pl-9 text-sm h-9" data-testid="suivi-search" />
      </div>

      {/* Email timeline by client */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Mail size={32} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">Aucun email envoye</p>
          <p className="text-xs mt-1">Les emails apparaitront ici apres envoi depuis les Menus Groupe</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(client => {
            const lastEmail = client.emails[0];
            const menuInfo = menus.find(m => m.emails_envoyes?.some(me => me.to === client.to));
            // Get all email types sent to this client from menus
            const sentTypes = new Set();
            menus.forEach(m => {
              (m.emails_envoyes || []).forEach(me => {
                if (me.to === client.to) sentTypes.add(me.type);
              });
            });

            return (
              <div key={client.to} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-all" data-testid={`client-${client.to}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-sm font-bold text-gray-900">{client.client_nom || client.to}</p>
                    <p className="text-[10px] text-gray-400">{client.to}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">{client.emails.length} email{client.emails.length > 1 ? 's' : ''}</p>
                    {menuInfo && <p className="text-[10px] text-[#009893] font-medium">{menuInfo.nom}</p>}
                  </div>
                </div>

                {/* Progress bar showing which types have been sent */}
                <div className="flex gap-1 mb-3">
                  {Object.entries(MAIL_TYPE_LABELS).map(([type, config]) => {
                    const sent = sentTypes.has(type);
                    return (
                      <div key={type} className={`flex-1 h-2 rounded-full ${sent ? config.color : 'bg-gray-100'}`} title={`${config.label}${sent ? ' (envoye)' : ''}`} />
                    );
                  })}
                </div>
                <div className="flex gap-1 mb-3">
                  {Object.entries(MAIL_TYPE_LABELS).map(([type, config]) => {
                    const sent = sentTypes.has(type);
                    return (
                      <p key={type} className={`flex-1 text-center text-[8px] ${sent ? config.textColor + ' font-bold' : 'text-gray-300'}`}>{config.label}</p>
                    );
                  })}
                </div>

                {/* Email list */}
                <div className="space-y-1.5">
                  {client.emails.map((e, i) => (
                    <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#009893] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-medium text-gray-700 truncate">{e.subject}</p>
                      </div>
                      <span className="text-[9px] text-gray-400 shrink-0">{formatDate(e.date)}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
